import { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { AgGridAtom, ModalAtom, TextFieldAtom } from "../../../components/atoms/atom";
import TableSkeleton from "../../../components/atoms/table-skeleton/TableSkeleton";
import { FilterBar } from "../../../components/molecule/molecule";
import NoPermissionAlert from "../../../components/NoPermissionAlert";
import PermissionPresetSelector from "../../../components/project/PermissionPresetSelector";
import ProjectHeader from "../../../components/ProjectHeader";
import {
	createDefaultPermissions,
	isTradeAccessIncomplete,
	normalizePermissions,
	PROJECT_AREA,
	PROJECT_ACTION,
} from "../../../constant/projectPermissions";
import useProjectPermission from "../../../hooks/useProjectPermission";
import {
	useCreatePresetMutation,
	useDeletePresetMutation,
	useListPresetsQuery,
	useUpdatePresetMutation,
} from "../../../store/services/projectPermissionPresets";
import { getSearchAddFilter } from "../../../utils/get-filter-items";
import { createActionColumn } from "../../../utils/grid-columns";
import { getFormattedDate, showToast } from "../../../utils/method";
import { searchData } from "../../../utils/search";
import PermissionsForm from "../_shared/permissions-form";
import "./permission-presets.scss";

const getPresetColumns = (t, handlers, perm) => [
	{
		field: "createdAt",
		headerName: t("projects.provided"),
		maxWidth: 120,
		valueFormatter: (params) => getFormattedDate(params.value),
	},
	{
		field: "name",
		headerName: t("projects.permissions.preset-name"),
		width: 200,
		unSortIcon: true,
	},
	{
		field: "description",
		headerName: t("projects.permissions.preset-description"),
		minWidth: 400,
		unSortIcon: true,
		valueGetter: (params) => params.data.description || "-",
	},
	{
		field: "isSystem",
		headerName: t("projects.permissions.preset-type"),
		maxWidth: 140,
		valueGetter: (params) =>
			params.value
				? t("projects.permissions.preset-system")
				: t("projects.permissions.preset-custom"),
	},
	createActionColumn({
		t,
		actions: [
			{
				onClick: handlers.onEdit,
				label: "common.edit",
				show: (row) => perm.canEditPermissions && !row.isSystem,
			},
			{
				onClick: handlers.onDelete,
				label: "help.action-delete",
				danger: true,
				show: (row) => perm.canEditPermissions && !row.isSystem,
			},
		],
	}),
];

function PermissionPresetsPage() {
	const { t } = useTranslation();
	const { projectId } = useParams();
	const { can, isProjectAdmin, isLoading: permissionLoading } =
		useProjectPermission();
	const canViewMembers = !permissionLoading && can(PROJECT_AREA.MEMBERS, PROJECT_ACTION.VIEW);
	// Editing project permission presets is Project-Admin-only.
	const canEditPermissions = isProjectAdmin;

	const presetPerm = useMemo(
		() => ({ canEditPermissions }),
		[canEditPermissions],
	);

	const projectDetails = useSelector((state) => state.projectDetails.project);
	const { data, isLoading, isFetching } = useListPresetsQuery(projectId, {
		skip: !projectId || !canViewMembers,
	});
	const [createPreset, { isLoading: isCreating }] = useCreatePresetMutation();
	const [updatePreset, { isLoading: isUpdating }] = useUpdatePresetMutation();
	const [deletePreset, { isLoading: isDeleting }] = useDeletePresetMutation();

	const presets = useMemo(() => data?.items ?? [], [data?.items]);
	const saving = isCreating || isUpdating;

	const [filteredPresets, setFilteredPresets] = useState([]);
	const filterConfiguration = useMemo(
		() =>
			getSearchAddFilter({
				addTextKey: "projects.permissions.create-preset",
				hideAddButton: !canEditPermissions,
			}),
		[canEditPermissions],
	);
	const [filterItems, setFilterItems] = useState(filterConfiguration);
	const [showEditModal, setShowEditModal] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [editingPreset, setEditingPreset] = useState(null);
	const [editName, setEditName] = useState("");
	const [editDescription, setEditDescription] = useState("");
	const [editPermissions, setEditPermissions] = useState(null);
	const [presetToDelete, setPresetToDelete] = useState(null);

	useEffect(() => {
		setFilterItems(filterConfiguration);
	}, [filterConfiguration]);

	useEffect(() => {
		setFilteredPresets(presets);
	}, [presets]);

	const openEditModal = useCallback((preset) => {
		setEditingPreset(preset);
		setEditName(preset.name || "");
		setEditDescription(preset.description || "");
		setEditPermissions(
			normalizePermissions(
				preset.permissions || createDefaultPermissions(),
			),
		);
		setShowEditModal(true);
	}, []);

	const openCreateModal = useCallback(() => {
		openEditModal({
			id: null,
			name: "",
			description: "",
			permissions: createDefaultPermissions(),
			isSystem: false,
		});
	}, [openEditModal]);

	const handleSave = async () => {
		if (!canEditPermissions || !editName.trim()) {
			showToast(t("projects.permissions.error-name-required"), {
				type: "error",
			});
			return;
		}
		try {
			const payload = {
				name: editName.trim(),
				description: editDescription.trim() || null,
				permissions: editPermissions,
			};
			if (editingPreset?.id) {
				await updatePreset({
					projectId,
					id: editingPreset.id,
					...payload,
				}).unwrap();
				showToast(t("projects.permissions.preset-updated-success"), {
					type: "success",
				});
			} else {
				await createPreset({ projectId, ...payload }).unwrap();
				showToast(t("projects.permissions.preset-created-success"), {
					type: "success",
				});
			}
			setShowEditModal(false);
		} catch (error) {
			showToast(error?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	const handleDelete = async () => {
		if (!presetToDelete) return;
		try {
			await deletePreset({
				projectId,
				id: presetToDelete.id,
			}).unwrap();
			showToast(t("projects.permissions.preset-deleted-success"), {
				type: "success",
			});
			setShowDeleteModal(false);
			setPresetToDelete(null);
		} catch (error) {
			showToast(error?.data?.message || t("errors.general"), {
				type: "error",
			});
		}
	};

	const handleEdit = useCallback(
		(row) => {
			if (!canEditPermissions || row.isSystem) return;
			openEditModal(row);
		},
		[canEditPermissions, openEditModal],
	);

	const handleDeleteClick = useCallback(
		(row) => {
			if (!canEditPermissions || row.isSystem) return;
			setPresetToDelete(row);
			setShowDeleteModal(true);
		},
		[canEditPermissions],
	);

	const actionHandlers = useMemo(
		() => ({
			onEdit: handleEdit,
			onDelete: handleDeleteClick,
		}),
		[handleEdit, handleDeleteClick],
	);

	const columns = useMemo(
		() => getPresetColumns(t, actionHandlers, presetPerm),
		[t, actionHandlers, presetPerm],
	);

	const onFilterAction = (index, key, info) => {
		const localFilterItems = [...filterItems];
		localFilterItems[index].value = info?.value;
		setFilterItems(localFilterItems);

		let filtered = [...presets];
		localFilterItems.forEach((item) => {
			if (item.key === "search" && item.value) {
				filtered = searchData(item.value, filtered, columns);
			}
		});
		setFilteredPresets(filtered);

		if (key === "add" && canEditPermissions) {
			openCreateModal();
		}
	};

	const isNewPreset = !editingPreset?.id;
	const loading = isLoading || isFetching;

	if (permissionLoading) {
		return (
			<div className="permission-presets-page">
				<ProjectHeader
					projectName={projectDetails?.name ?? ""}
					tabTitle={t("projects.permission-presets.title")}
				/>
				<TableSkeleton />
			</div>
		);
	}

	if (!canViewMembers) {
		return (
			<div className="permission-presets-page">
				<ProjectHeader
					projectName={projectDetails?.name ?? ""}
					tabTitle={t("projects.permission-presets.title")}
				/>
				<NoPermissionAlert />
			</div>
		);
	}

	return (
		<div className="permission-presets-page">
			<ProjectHeader
				projectName={projectDetails?.name}
				tabTitle={t("projects.permission-presets.title")}
			/>
			<div className="page-container">
				<FilterBar items={filterItems} onFilterAction={onFilterAction} />

				<div className="flex-grow-1 ag-grid-parent">
					{loading && filteredPresets.length === 0 ? (
						<TableSkeleton />
					) : (
						<AgGridAtom
							columnDefs={columns}
							rowData={filteredPresets}
							overlayNoRowsTemplate={`<span class="ag-overlay-no-rows-center">${t("projects.permissions.no-presets")}</span>`}
							defaultColDef={{
								flex: 1,
								sortable: true,
								filter: false,
							}}
						/>
					)}
				</div>

				<ModalAtom
					show={showEditModal}
					handleClose={() => setShowEditModal(false)}
					title={
						isNewPreset
							? t("projects.permissions.create-preset-title")
							: t("projects.permissions.edit-preset-title")
					}
					saveText={saving ? "common.saving" : "common.save"}
					disabled={saving || !canEditPermissions}
					submitDisabled={isTradeAccessIncomplete(editPermissions)}
					handleSubmit={handleSave}
					body={
						editPermissions && (
							<div>
								<Row className="g-3 mx-0 mb-3">
									<Col xs={12} md={isNewPreset ? 4 : 6}>
										<TextFieldAtom
											label={t("projects.permissions.preset-name")}
											maxLength={50}
											value={editName}
											onChange={(e) =>
												setEditName(e.target.value)
											}
										/>
									</Col>
									<Col xs={12} md={isNewPreset ? 4 : 6}>
										<TextFieldAtom
											label={t(
												"projects.permissions.preset-description",
											)}
											maxLength={255}
											value={editDescription}
											onChange={(e) =>
												setEditDescription(e.target.value)
											}
										/>
									</Col>
									{isNewPreset && (
										<Col xs={12} md={4}>
											<PermissionPresetSelector
												projectId={projectId}
												onPresetApplied={({
													permissions: perms,
												}) => setEditPermissions(perms)}
											/>
										</Col>
									)}
								</Row>
								<PermissionsForm
									projectId={projectId}
									permissions={editPermissions}
									onChange={setEditPermissions}
								/>
							</div>
						)
					}
				/>

				<ModalAtom
					show={showDeleteModal}
					size="sm"
					handleClose={() => setShowDeleteModal(false)}
					title={t("projects.permissions.delete-preset-title")}
					saveText={isDeleting ? "common.saving" : "common.delete"}
					disabled={isDeleting}
					handleSubmit={handleDelete}
					body={
						<p>
							{t("projects.permissions.delete-preset-warning", {
								name: presetToDelete?.name || "",
							})}
						</p>
					}
				/>
			</div>
		</div>
	);
}

export default PermissionPresetsPage;
